import React, { useState, useEffect } from 'react';
import { Button, Col, Row, Form, OverlayTrigger, Popover, Spinner, Card } from 'react-bootstrap';
import { Header, Segment, Loader, Dimmer, Image } from 'semantic-ui-react'
import { TextareaAutosize } from '@material-ui/core';
import { useHistory, useParams } from "react-router-dom";
import { API, Auth } from 'aws-amplify';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import { makeStyles } from '@material-ui/core/styles';
import { DataGrid, GridToolbar, GridToolbarContainer } from '@mui/x-data-grid';
import { Typography as ATypography } from 'antd'

const useStyles = makeStyles(() => ({
	root: {
		alignItems: 'center',
		lineHeight: '24px',
		width: '100%',
		height: '100%',
		position: 'relative',
		display: 'flex',
		'& .cellValue': {
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
	},
}));

function isOverflown(element) {
	return (
		element.scrollHeight > element.clientHeight ||
		element.scrollWidth > element.clientWidth
	);
}

const GridCellExpand = React.memo(function GridCellExpand(props) {
	const { width, value } = props;
	const wrapper = React.useRef(null);
	const cellDiv = React.useRef(null);
	const cellValue = React.useRef(null);
	const [anchorEl, setAnchorEl] = React.useState(null);
	const classes = useStyles();
	const [showFullCell, setShowFullCell] = React.useState(false);
	const [showPopper, setShowPopper] = React.useState(false);

	const handleMouseEnter = () => {
		const isCurrentlyOverflown = isOverflown(cellValue.current);
		setShowPopper(isCurrentlyOverflown);
		setAnchorEl(cellDiv.current);
		setShowFullCell(true);
	};

	const handleMouseLeave = () => {
		setShowFullCell(false);
	};

	React.useEffect(() => {
		if (!showFullCell) {
			return undefined;
		}

		function handleKeyDown(nativeEvent) {
			// IE11, Edge (prior to using Bink?) use 'Esc'
			if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
				setShowFullCell(false);
			}
		}

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [setShowFullCell, showFullCell]);

	return (
		<div
			ref={wrapper}
			className={classes.root}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<div
				ref={cellDiv}
				style={{
					height: 1,
					width,
					display: 'block',
					position: 'absolute',
					top: 0,
				}}
			/>
			<div ref={cellValue} className="cellValue">
				{value}
			</div>
			{showPopper && (
				<Popper
					open={showFullCell && anchorEl !== null}
					anchorEl={anchorEl}
					style={{ width, marginLeft: -17 }}
				>
					<Paper
						elevation={1}
						style={{ minHeight: wrapper.current.offsetHeight - 3 }}
					>
						<Typography variant="body2" style={{ padding: 8 }}>
							{value}
						</Typography>
					</Paper>
				</Popper>
			)}
		</div>
	);
});

GridCellExpand.propTypes = {
	value: PropTypes.string.isRequired,
	width: PropTypes.number.isRequired,
};

function renderCellExpand(params) {
	return (
		<GridCellExpand
			value={params.value ? params.value.toString() : ''}
			width={params.colDef.width}
		/>
	);
}

renderCellExpand.propTypes = {
	/**
	 * The column of the row that the current cell belongs to.
	 */
	colDef: PropTypes.any.isRequired,
	/**
	 * The cell value, but if the column has valueGetter, use getValue.
	 */
	value: PropTypes.oneOfType([
		PropTypes.instanceOf(Date),
		PropTypes.number,
		PropTypes.object,
		PropTypes.string,
		PropTypes.bool,
	]),
};

export default function Blast_Detail() {

	let { id } = useParams();

	const Bucket = "https://ugtdb-tib-prod-s3tibprodugtdb5094d93f-1rmju6fmzr79j.s3.amazonaws.com/";

	const DBHeader1 = ["id", "Subject id", "% identity", "alignment length", "mismatches", "gap openings", "q.start", "q.end", "s.start", "s.end", "e-value", "bit score", "P(A)"];
	const DBHeader2 = ["id", "Sugar donor prediction", "Acceptor prediction"];

	const columns1 = [
		{
			field: 'id',
			headerName: 'No',
			flex: 1,
			hide: true,
		},
		{
			field: 'key',
			headerName: 'Query_ID',
			flex: 1,
			renderCell: renderCellExpand,
		},
		{
			field: 'Subject id',
			headerName: 'Subject id',
			flex: 1,
			renderCell: renderCellExpand,
		},
		{
			field: '% identity',
			headerName: '% identity',
			flex: 1,
			renderCell: renderCellExpand,
		},
		{
			field: 'alignment length',
			headerName: 'alignment length',
			flex: 1,
			renderCell: renderCellExpand,
		},
		{
			field: 'mismatches',
			headerName: 'mismatches',
			flex: 1,
			renderCell: renderCellExpand,
		},
		{
			field: 'gap openings',
			headerName: 'gap openings',
			flex: 1,
			renderCell: renderCellExpand,
		},
		{
			field: 'q.start',
			headerName: 'q.start',
			flex: 1,
			hide: true,
			renderCell: renderCellExpand,
		},
		{
			field: 'q.end',
			headerName: 'q.end',
			flex: 1,
			hide: true,
			renderCell: renderCellExpand,
		},
		{
			field: 's.start',
			headerName: 's.start',
			flex: 1,
			hide: true,
			renderCell: renderCellExpand,
		},
		{
			field: 's.end',
			headerName: 's.end',
			flex: 1,
			hide: true,
			renderCell: renderCellExpand,
		},
		{
			field: 'e-value',
			headerName: 'e-value',
			flex: 1,
			hide: true,
			renderCell: renderCellExpand,
		},
		{
			field: 'bit score',
			headerName: 'bit score',
			flex: 1,
			hide: true,
			renderCell: renderCellExpand,
		},
		{
			field: 'P(A)',
			headerName: 'P(A)',
			flex: 1,
			hide: true,
			renderCell: renderCellExpand,
		},
	];

	const columns2 = [
		// { 
		//     field: 'id', 
		//     headerName: 'id',
		//     flex: 1, 
		// 	hide: true,
		// },
		{
			field: 'id',
			headerName: 'Query',
			flex: 1,
			renderCell: renderCellExpand,
		},
		{
			field: 'Sugar donor prediction',
			headerName: 'Sugar donor prediction',
			flex: 2,
			renderCell: renderCellExpand,
		},
		{
			field: 'Acceptor prediction',
			headerName: 'Acceptor prediction',
			flex: 3,
			renderCell: renderCellExpand,
		},
	];

	const [Type, setType] = useState();
	const [Error, setError] = useState();
	const [Status, setStatus] = useState();

	const [preview, setPreview] = useState();
	const [items, setItems] = useState([]);


	const [result, setResult] = useState();

	const [isLoaded, setIsLoaded] = useState(false);
	const [display1, setDisplay1] = useState("none");
	const [display2, setDisplay2] = useState("none");
	const [display3, setDisplay3] = useState("none");
	const [display4, setDisplay4] = useState("none");

	const Processing = "Processing...";

	const [OUT_Blast, setOUT_Blast] = useState();
	const [OUT_UGTs, setOUT_UGTs] = useState();
	const [FUN_OUT, setFUN_OUT] = useState();
	const [errorInfo, seterrorInfo] = useState();

	const [disable_OUT_Blast, setDisable_OUT_Blast] = useState(false);
	const [disable_OUT_UGTs, setDisable_OUT_UGTs] = useState(false);
	const [disable_FUN_OUT, setDisable_FUN_OUT] = useState(false);

	const [DBHeader, setDBHeader] = useState();


	useEffect(() => {
		if (Type === "blast") {
			setDBHeader(DBHeader1);
		} else {
			setDBHeader(DBHeader2);
		}
	}, [Type])


	useEffect(() => {
		if (id !== null) {
			getResult(id);
		}
	}, [id])

	async function getResult(props) {
		// Amplify.Logger.LOG_LEVEL = "DEBUG";
		const session = await Auth.currentCredentials();
		// console.log("session:",session);
		const apiRequest = {

			response: false,
			headers: {
				"Content-Type": "application/json",
			}

		};


		return await API
			.get("UGT", "/result/" + props, apiRequest)
			.then(
				(result) => {

					console.log(result);

					let current_id = result.ID;
					let current_name = result.Name;
					let current_type = result.Type;
					let current_ts = result.Timestamp;
					let current_status = result.Status;
					let current_result = result.Result;
					let current_error = result.Error;
					let current_Blast = result.Blast;
					let current_UGTs = result.UGTs;
					let current_Web = result.Web;
					let current_FUN = result.FUN_pre;

					setStatus(current_status);
					setType(current_type);
					setError(current_error);
					setOUT_Blast(Bucket + current_Blast);
					setOUT_UGTs(Bucket + current_UGTs);
					setPreview(Bucket + current_Web);
					setFUN_OUT(Bucket + current_FUN);
				},

				(error) => {
					console.log("error:", error);
				}
			);
	}


	useEffect(() => {
		if (isLoaded === true) {
			if (Error !== undefined && Error !== "") {
				setDisplay3("block");
				setDisplay1("none");
				setDisplay2("none");
			} else if (Status !== "running") {
				switch (Type) {
					case "blast":
						setDisplay1("block");
						setDisplay2("none");
						setDisable_OUT_Blast(false);
						setDisable_OUT_UGTs(false);
						setDisable_FUN_OUT(true);

						break;

					case "donor prediction":
						setDisplay2("block");
						setDisplay1("none");
						setDisable_OUT_Blast(true);
						setDisable_OUT_UGTs(true);
						setDisable_FUN_OUT(false);
						break;

					case "virtual screen":
						setDisplay2("block");
						setDisplay1("none");
						setDisable_OUT_Blast(true);
						setDisable_OUT_UGTs(true);
						setDisable_FUN_OUT(true);
						break;

					default:
						break;
				}
			} else {
				switch (Type) {
					case "blast":
						setDisplay1("block");
						setDisplay2("none");
						setDisable_OUT_Blast(true);
						setDisable_OUT_UGTs(true);
						setDisable_FUN_OUT(true);
						break;

					case "donor prediction":
						setDisplay2("block");
						setDisplay1("none");
						setDisable_OUT_Blast(true);
						setDisable_OUT_UGTs(true);
						setDisable_FUN_OUT(true);
						break;

					case "virtual screen":
						setDisplay2("block");
						setDisplay1("none");
						setDisable_OUT_Blast(true);
						setDisable_OUT_UGTs(true);
						setDisable_FUN_OUT(true);
						break;

					default:
						break;
				}
			}

		}
	}, [isLoaded])

	useEffect(() => {
		if (preview !== undefined) {
			if (preview !== Bucket) {
				fetch(preview, {
					method: 'GET',
					mode: 'cors',
					headers: {
						'Accept': 'application/text',/* 格式限制：json、文本、其他格式 */
						'Content-Type': 'application/text',/* 请求内容类型 */
					}
				})
					.then(res => res.text())
					.then(
						(data) => {


							let current = String(data);
							// console.log(current);
							setResult(current);
							setIsLoaded(true);
							setItems(toJson(current))
							console.log("toJSON", toJson(current));

						},
						(err) => {
							console.log(err);

						}
					);
			}
		}
	}, [preview])

	useEffect(() => {
		if (Error !== undefined) {
			let current = Bucket + Error;
			fetch(current, {
				method: 'GET',
				mode: 'cors',
				headers: {
					'Accept': 'application/text',/* 格式限制：json、文本、其他格式 */
					'Content-Type': 'application/text',/* 请求内容类型 */
				}
			})
				.then(res => res.text())
				.then(
					(data) => {


						let current = String(data);
						// console.log(current);
						seterrorInfo(current);
						setIsLoaded(true);
					},
					(err) => {
						console.log(err);


					}
				);
		}
	}, [Error])


	function toJson(params) {

		let symbol = "	";
		let currentItems = [];
		let currentArr = params.split("\n");
		let count = 1;
		for (let i = 1; i < currentArr.length - 1; i++) {
			let current = currentArr[i].split(symbol);
			let currentItem = {};
			//   currentItem["id"] = count;
			for (let j = 0; j < DBHeader.length; j++) {
				if (current[j] !== undefined) {
					currentItem[DBHeader[j]] = current[j];
				} else {
					currentItem[DBHeader[j]] = current[j];
				}
			}
			currentItem["key"] = currentItem.id;
			currentItem["id"] = i;
			currentItems.push(currentItem);
			count++;
		}
		return currentItems;
	}

	function CustomToolbar() {
		return (
			<GridToolbarContainer>
				<GridToolbar />

			</GridToolbarContainer>
		);
	}

	return (
		<div style={{ background: "#FFFFFF" }}>
			<Row>
				<Col >
				</Col>
				<Col md={10}>

					<div style={{ height: "50px" }}>
					</div>
					<div>
						<Card>
							<Card.Header>
								<Header textAlign='center'>
									<span style={{ color: "#6495ED" }} >P450 Blast Detail</span>
								</Header>
							</Card.Header>
							<Card.Body>
								<div style={{ margin: "25px 75px 25px 75px" }}>
									<ATypography.Paragraph italic style={{ textAlign: "justify" }}><blockquote>Search the user-submitted P450 sequence against P450-100 and FunP450. P450-100 contains one million non-redundant P450 sequences, while FunP450 represents all functionally characterized P450 enzymes.</blockquote></ATypography.Paragraph>
								</div>
								<Segment basic >
									{
										isLoaded
											?
											<>
												<div style={{ display: display1 }} >
													<div style={{ height: "2rem" }} />


													<Form>
														{

															result !== undefined
																?
																<>

																	<DataGrid
																		rows={items}
																		columns={columns1}
																		autoHeight
																		autoPageSize
																		components={{
																			Toolbar: CustomToolbar,
																		}}
																	/>
																</>
																:
																<Form.Group as={Row}>

																	<Form.Label column sm="3" className="top3">
																		P450 Blast Detail
																	</Form.Label>
																	<Col sm="9">
																		<TextareaAutosize aria-label="minimum height" rowsMin={3} value={Processing} style={{ width: "100%" }} disabled />
																	</Col>

																</Form.Group>
														}
														<Segment basic>
															<Row>
																<Col sm={6} className="top2" style={{ textAlign: "center" }}>
																	<OverlayTrigger
																		key='bottom'
																		placement='bottom'
																		overlay={
																			<Popover id="popover-basic">
																				<Popover.Title >The P450 Blast result</Popover.Title>
																			</Popover>
																		}
																	>
																		<Button target="_blank" href={OUT_Blast} disabled={disable_OUT_Blast}>OUT_Blast.txt</Button>
																	</OverlayTrigger>

																</Col>
																<Col sm={6} className="top2" style={{ textAlign: "center" }}>
																	<OverlayTrigger
																		key='bottom'
																		placement='bottom'
																		overlay={
																			<Popover id="popover-basic">
																				<Popover.Title >The protein found in P450 database</Popover.Title>
																			</Popover>
																		}
																	>
																		<Button target="_blank" href={OUT_UGTs} disabled={disable_OUT_UGTs}>OUT_P450.txt</Button>
																	</OverlayTrigger>

																</Col>
															</Row>
														</Segment>
														{/* <div style={{height:"25px"}} /> */}
														<Row>
															<Col sm={1} />
															<Col sm={10}>
																<Header size='medium' className="Title2"><span style={{ color: "#3366CC" }}>Detail: </span><small>It would display up to 10 results for each sequence submitted on this web page, but the complete information can be downloaded from the link (OUT_Blast.txt, OUT_P450.txt)</small></Header>
															</Col>
															<Col sm={1} />
														</Row>

														<div style={{ height: "25px" }} />
													</Form>


												</div>

												<div style={{ display: display2 }}>
													<div style={{ height: "2rem" }} />
													<Form>
														{

															result !== undefined
																?
																<>
																	<div style={{ textAlign: "center" }}>
																		<h2 ><span style={{ color: "#3366CC" }}>P450 Blast Detail</span></h2 >
																	</div>
																	<div style={{ height: "1rem" }} />
																	<DataGrid
																		rows={items}
																		columns={columns2}
																		autoHeight
																		autoPageSize
																		components={{
																			Toolbar: CustomToolbar,
																		}}
																	/>
																</>
																:
																<Form.Group as={Row}>

																	<Form.Label column sm="3" className="top3">
																		Result
																	</Form.Label>
																	<Col sm="9">
																		<TextareaAutosize aria-label="minimum height" rowsMin={3} value={Processing} style={{ width: "100%" }} disabled />
																	</Col>

																</Form.Group>
														}

														<Row>
															<Col sm={3} className="top2" />
															<Col sm={6} className="top2">
																<Segment basic>
																	<Button target="_blank" href={FUN_OUT} disabled={disable_FUN_OUT}>FUN_OUT.txt</Button>
																</Segment>
															</Col>
															<Col sm={3} className="top2" />
														</Row>
														{/* <div style={{height:"25px"}} /> */}
														<Row>
															<Col sm={1} />
															<Col sm={10}>
																<Header size='medium' className="Title2"><span style={{ color: "#3366CC" }}>Detail: </span><small>It would display up to 10 results for the sequence submitted on this web page, but the complete information can be downloaded from the link (FUN_OUT.txt)</small></Header>
															</Col>
															<Col sm={1} />
														</Row>

														<div style={{ height: "25px" }} />
													</Form>


												</div>

												<div style={{ display: display3 }}>
													<div style={{ height: "2rem" }} />
													<Form>
														<Form.Group as={Row}>

															<Form.Label column sm="3" className="top3">
																ERROR Information
															</Form.Label>
															<Col sm="9">
																<TextareaAutosize aria-label="minimum height" rowsMin={3} value={errorInfo} style={{ width: "100%" }} disabled />
															</Col>

														</Form.Group>
													</Form>


												</div>




												<div style={{ display: display4 }}>
													<Segment placeholder textAlign="center">

														<row><Spinner animation="border" variant="dark" /><h1>Processing</h1></row>

													</Segment>
												</div>
											</>
											:
											<Segment>
												<Dimmer active inverted>
													<Loader size='medium'>Loading</Loader>
												</Dimmer>

												<Image src='../paragraph.png' />
											</Segment>
									}

								</Segment>


							</Card.Body>
						</Card>




					</div>


					<div>


						<div>

						</div>



					</div>
					<div style={{

						height: "50px"
					}}>
					</div>
				</Col>
				<Col >
				</Col>
			</Row>
		</div>

	);
}
